<script setup lang="ts">
/**
 * Keuzelijst. Vervangt `<v-input type="select">`.
 *
 * Kort (tot SEARCHABLE_SELECT_THRESHOLD opties): een native `<select>`, met optgroups.
 * Langer: automatisch een doorzoekbare VCombobox, net als VInput sinds GH #6826. Met
 * `searchable` forceer je het een of het ander. Optgroups blijven ook doorzoekbaar
 * bestaan als koppen in de lijst (GH #7020); enkel uitgeschakelde opties houden de
 * select native.
 */
import { trans } from 'laravel-vue-i18n'
import { computed, nextTick, ref, useSlots } from 'vue'
import VCombobox from './VCombobox.vue'
import VDisplayValue from './VDisplayValue.vue'
import VFormField from './VFormField.vue'
import {
    isOptGroup,
    type DisplayableProps,
    type FormFieldBaseProps,
    type SelectOption,
    type SelectValue,
} from './types'
import { controlClasses, useFieldErrors, useFormFieldBindings, useSplitAttrs } from './useFormField'

defineOptions({ inheritAttrs: false })

const modelValue = defineModel<string | number | null>()

const props = withDefaults(
    defineProps<
        FormFieldBaseProps &
            DisplayableProps & {
                /**
                 * VInput: `values`. Ook een object (bv. een PHP-array met niet-opeenvolgende
                 * sleutels komt als JSON-object binnen): dan tellen zijn waarden als opties.
                 */
                options?: SelectOption[] | Record<string, SelectOption>
                /** Tekst van de lege keuze; standaard "Maak een keuze". */
                placeholder?: string | null
                showPlaceholder?: boolean
                /** Optienamen zijn vertaalsleutels. */
                translated?: boolean
                /**
                 * Zoekveld. null = automatisch vanaf SEARCHABLE_SELECT_THRESHOLD opties;
                 * true/false forceert het (bv. false voor de btw-keuze op documentregels).
                 */
                searchable?: boolean | null
                /**
                 * Minimale breedte van de doorzoekbare lijst in px, voor een smal veld
                 * met lange optienamen (de btw-landcode in een prefix, GH #7020).
                 */
                listMinWidth?: number | null
            }
    >(),
    {
        options: () => [],
        placeholder: null,
        showPlaceholder: true,
        translated: false,
        searchable: null,
        listMinWidth: null,
        showErrors: true,
    }
)

const slots = useSlots()
const { rootAttrs, controlAttrs } = useSplitAttrs()
const errors = useFieldErrors(props)
const fieldBindings = useFormFieldBindings(props, errors)

const inputRef = ref<HTMLSelectElement | null>(null)

function focus(): void {
    // In de doorzoekbare modus is er geen <select>: focus het zoekveld.
    ;(inputRef.value ?? searchableRef.value?.querySelector('input'))?.focus()
}

defineExpose({ inputRef, focus })

const classes = computed<string>(() =>
    controlClasses({
        inputClasses: props.inputClasses,
        invalid: props.invalid,
        hasPrefix: !!slots.prefix,
        hasSuffix: !!slots.suffix,
    })
)

/**
 * Geen lege keuze tonen als `null` zelf een geldige optie is (zoals bij payment_info):
 * anders staan er twee opties met dezelfde waarde.
 */
/**
 * Opties altijd als lijst. VInput liep met v-for over `values` en slikte zo ook een
 * object; de btw-landen (selectValues.vat_countries) komen als object binnen en lieten
 * "Klant toevoegen" crashen (`props.options.some is not a function`, 25/09/2026).
 */
const optionList = computed<SelectOption[]>(() =>
    Array.isArray(props.options) ? props.options : Object.values(props.options ?? {})
)

const placeholderVisible = computed<boolean>(() => {
    if (!props.showPlaceholder) {
        return false
    }

    return !optionList.value.some((option) => !isOptGroup(option) && option.key === null)
})

// --- Doorzoekbare select (GH #6826, overgenomen uit VInput) ---
//
// Een gewone <select> met tientallen opties (klanten, landen, rekeningen, ...) was
// enkel met scrollen te bedienen. Vanaf deze drempel wordt het een combobox met
// zoekveld; korte lijsten (ja/nee, statussen, btw) blijven native.
const SEARCHABLE_SELECT_THRESHOLD = 10

/**
 * Hoeveel opties de doorzoekbare lijst tegelijk rendert; de rest vindt men door verder
 * te typen (melding onderaan de lijst). Eerst mountte de combobox ALLE opties, en
 * Headless UI sorteert bij elke (de)registratie van een optie de hele lijst: bij
 * duizenden klanten of projecten (tijd- en artikelregistratie) bevroor de tab
 * seconden lang bij het openen (review #6967 R026, GH #6972). Zoeken gebeurt nog
 * steeds over alle opties.
 *
 * Bewust 300 en niet 100: middelgrote alfabetische lijsten moeten volledig te scrollen
 * blijven. Met 100 stopte de landenkiezer van AddressFields (264 landen, ±15 schermen)
 * rond de K, waren Nederland, Spanje en het Verenigd Koninkrijk enkel nog via typen te
 * vinden en stond een gekozen land onderaan buiten de alfabetische volgorde. De kost
 * groeit kwadratisch: 300 opties is ±2% van de gemeten 2000 (±30 ms i.p.v. 1,4 s).
 */
const SEARCHABLE_MAX_RENDERED_OPTIONS = 300

// Headless UI kan null niet onderscheiden van "niets gekozen"; een null-optie ("Geen",
// "Alle") krijgt daarom een vaste stand-in.
const NULL_OPTION_KEY = '__null__'

type SearchableOption = {
    key: string | number
    name: string
    shortName: string
    group: string | null
}

type GroupedSelectValue = SelectValue & { group: string | null }

/**
 * Alle kiesbare opties, ook die in een optgroup, met hun groepslabel erbij.
 *
 * Eerst bleef elke lijst met optgroups een native <select> zonder zoekveld. De
 * btw-landcode (±130 codes in 9 regio's) was daardoor enkel met scrollen te vinden en
 * "EU" (non-Union OSS) vond niemand terug (GH #7020).
 */
const flatOptions = computed<GroupedSelectValue[]>(() =>
    optionList.value.flatMap<GroupedSelectValue>((option) =>
        isOptGroup(option)
            ? option.values.map((value) => ({ ...value, group: option.key }))
            : [{ ...option, group: null }]
    )
)

const hasGroups = computed<boolean>(() => optionList.value.some(isOptGroup))

const isSearchable = computed<boolean>(() => {
    if (props.searchable === false) {
        return false
    }
    // Uitgeschakelde opties kent de combobox niet; die blijven native.
    if (flatOptions.value.some((option) => option.disabled)) {
        return false
    }
    return props.searchable === true || flatOptions.value.length > SEARCHABLE_SELECT_THRESHOLD
})

const searchableOptions = computed<SearchableOption[]>(() =>
    flatOptions.value.map((option) => {
        const name = props.translated ? trans(option.name) : option.name
        return {
            key: option.key === null ? NULL_OPTION_KEY : (option.key as string | number),
            name,
            shortName: option.shortName ?? name,
            group: option.group,
        }
    })
)

const search = ref('')

const filteredOptions = computed<SearchableOption[]>(() => {
    const query = search.value.trim().toLowerCase()
    if (!query) {
        return searchableOptions.value
    }
    return searchableOptions.value.filter((option) =>
        String(option.name).toLowerCase().includes(query)
    )
})

const searchableRef = ref<HTMLElement | null>(null)

/**
 * Sleutels vergelijken op stringvorm, zoals een native <select> dat doet (Vue's
 * looseEqual): 7 en "7" zijn gelijk, maar '' is GEEN 0.
 *
 * Eerst stond hier `==`. Een leeg model ('', bv. een nieuwe herinnering met
 * `days: ''`) telde dan als sleutel 0: het veld toonde "Op de vervaldag" terwijl er
 * niets gekozen was, en die optie kiezen deed niets (de setter zag geen wijziging),
 * waarna bewaren een 422 gaf onder een veld met een zichtbare waarde
 * (review #6967 R025, GH #6972).
 */
function sameKey(
    a: string | number | null | undefined,
    b: string | number | null | undefined
): boolean {
    if (a === null || a === undefined || b === null || b === undefined) {
        return (a === null || a === undefined) && (b === null || b === undefined)
    }
    return String(a) === String(b)
}

// Het gekozen item komt uit de VOLLEDIGE lijst, niet uit de gefilterde: anders
// verdwijnt het label zodra de zoekterm de huidige keuze wegfiltert.
const searchableSelected = computed<SearchableOption | null>({
    get() {
        const key = modelValue.value ?? NULL_OPTION_KEY
        return searchableOptions.value.find((option) => sameKey(option.key, key)) ?? null
    },
    set(option) {
        // VCombobox zet het model op null zodra het zoekveld leeg is (ook tijdens het
        // typen). Zonder "Geen"/"Alle"-optie is null geen geldige keuze: behouden.
        if (!option && !searchableOptions.value.some((o) => o.key === NULL_OPTION_KEY)) {
            return
        }
        const next = !option || option.key === NULL_OPTION_KEY ? null : option.key
        search.value = ''
        // Enkel bij een echte wijziging: anders herlaadt bv. een filter zonder keuze.
        // Strikt via sameKey: met `==` gold 0 == '' en werd een keuze genegeerd (R025).
        if (sameKey(next, modelValue.value)) {
            return
        }
        modelValue.value = next
        // Een native <select> vuurt `change`; schermen hangen daar handlers aan
        // (filters, lijnherberekening). Na de model-update hetzelfde event sturen.
        nextTick(() => searchableRef.value?.dispatchEvent(new Event('change', { bubbles: true })))
    },
})

const searchablePlaceholder = computed<string>(
    () =>
        props.placeholder ??
        (placeholderVisible.value ? 'global.make_a_choice' : 'global.type_and_search')
)

/**
 * Het zoekveld vuurt zelf `change` bij typen; dat mag niet als keuze bij de
 * @change-handlers van het scherm aankomen. Enkel het event dat we na een keuze op de
 * wrapper zelf dispatchen, gaat door.
 */
function blockInnerChange(event: Event): void {
    if (event.target !== event.currentTarget) {
        event.stopImmediatePropagation()
    }
}

/**
 * Zoekterm wissen wanneer de lijst sluit zonder keuze (focus verlaat het veld, of
 * Escape). Eerst gebeurde dat enkel bij een keuze: het veld toonde na sluiten weer het
 * gekozen label, maar de lijst bleef op de oude term gefilterd en toonde bij heropenen
 * "Geen resultaten" of een ingekorte lijst (review #6967 R198, GH #6972). Een klik op
 * een optie of op het pijltje haalt de focus niet weg (Headless UI houdt die vast,
 * en het pijltje zit in deze wrapper).
 */
function clearSearchOnFocusOut(event: FocusEvent): void {
    const next = event.relatedTarget as Node | null
    if (next && searchableRef.value?.contains(next)) {
        return
    }
    search.value = ''
}

function clearSearch(): void {
    search.value = ''
}
</script>

<template>
    <VDisplayValue
        v-if="displayAs != null"
        v-bind="$attrs"
        :value="modelValue"
        :type="displayAs"
        :layout="layout"
        :align-right="inputClasses?.includes('text-right') ?? false"
    >
        <template v-if="$slots.suffix" #suffix><slot name="suffix"></slot></template>
    </VDisplayValue>

    <VFormField v-else v-bind="{ ...fieldBindings, ...rootAttrs }">
        <template v-if="$slots.prefix" #prefix><slot name="prefix"></slot></template>

        <div
            v-if="isSearchable"
            ref="searchableRef"
            class="v-select-searchable w-full"
            @change="blockInnerChange"
            @focusout="clearSearchOnFocusOut"
            @keydown.esc="clearSearch"
            v-bind="controlAttrs"
        >
            <!-- input-id: het label van VFormField wijst naar id ?? name (R199). -->
            <VCombobox
                v-model="searchableSelected"
                v-model:search-value="search"
                :items="filteredOptions"
                item-label="name"
                item-key="key"
                return-object
                :disabled="disabled"
                :placeholder="searchablePlaceholder"
                :input-id="id ?? name ?? null"
                :max-items="SEARCHABLE_MAX_RENDERED_OPTIONS"
                :item-group="hasGroups ? 'group' : null"
                item-display="shortName"
                :list-min-width="listMinWidth"
            />
        </div>

        <select
            v-else
            v-bind="controlAttrs"
            ref="inputRef"
            v-model="modelValue"
            :id="id ?? name ?? undefined"
            :name="name ?? undefined"
            :disabled="disabled"
            :class="['!border-gray-300', classes]"
        >
            <option v-if="placeholderVisible" :value="null" disabled>
                {{ placeholder ?? $t('global.make_a_choice') }}
            </option>
            <template v-for="option in optionList">
                <optgroup v-if="isOptGroup(option)" :key="option.key" :label="option.key">
                    <option
                        v-for="v in option.values"
                        :key="String(v.key)"
                        :value="v.key"
                        :disabled="v.disabled === true"
                    >
                        {{ translated ? $t(v.name) : v.name }}
                    </option>
                </optgroup>
                <option
                    v-else
                    :key="String(option.key)"
                    :value="option.key"
                    :disabled="option.disabled === true"
                >
                    {{ translated ? $t(option.name) : option.name }}
                </option>
            </template>
        </select>

        <template v-if="$slots.suffix" #suffix><slot name="suffix"></slot></template>
    </VFormField>
</template>

<style scoped lang="scss">
// Eigen pijltje met transparante achtergrond, zodat het leesbaar blijft op elke
// achtergrondkleur (o.a. in een prefix).
select {
    background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e");
    background-position: right 0.5rem center;
    background-repeat: no-repeat;
    background-size: 1.5em 1.5em;
    padding-right: 2.5rem;
}

// Een select in de prefix van een ander veld (bv. valuta vóór een bedrag).
.prefix select {
    @apply bg-gray-200;
}

// Doorzoekbare select in een prefix (bv. de btw-landcode, GH #7020): de prefix tekent
// zelf al de rand en de grijze achtergrond, een tweede kader erin oogt als een los veld.
.prefix .v-select-searchable :deep(.v-combobox > div) {
    @apply rounded-none border-0 bg-transparent;
}

.prefix .v-select-searchable :deep(.v-combobox input) {
    @apply bg-transparent;
}

.no-arrow select {
    @apply px-2;
    background-image: none;
}
</style>
